import React, { FC, useEffect, useState } from 'react';
import { Switch, Route, useLocation, Redirect } from 'react-router-dom';
import Header from './components/Header';
import Content from './components/Content';
import useStyles from './styles';
import Footer from '@shared/components/Footer';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import SplashScreen from '@src/shared/components/SplashScreen';
import useQueryParam from '@shared/hooks/use-queryparam';
import { languageQueryParam } from '@src/i18n';
import Couriers from '@src/pages/Couriers';
import Deliveries from '@src/pages/Deliveries';
import { isLanguageActive } from '../../../i18n';
import useFMFeatureFlag from '@src/shared/hooks/use-fm-featureflag';
import {
  JETFM_ACTIVATE_FEEDBACK_CAMPAIGN,
  JETFM_ACTIVATE_FEEDBACK_SURVEY,
  JETFM_MCP_VERSION_COMPARISON_VALUE,
  JETFM_MCP_VERSION_REFRESHER_IS_ACTIVE_VALUE,
  JETFM_SHOW_MANAGE_COURIERS,
} from '@src/shared/models';
import ConnectionNotifier from '@src/shared/components/ConnectionNotifier';
import useGetFeedback from '@src/shared/hooks/use-getfeedback';
import { useQueryNotificationList } from '@src/shared/queries';

import PIEAnnouncementBanner, {
  ANNOUNCEMENT_BANNER_BUTTON_POSITIONING,
} from '@src/shared/components/PIEAnnouncementBanner';
import DeliveryDashboard from '@src/pages/DeliveryDashboard';
import useAnnouncementBannerLogic from '@src/shared/hooks/use-anouncement-banner-logic';
import utilStorage from '@src/shared/services/util-storage';
import ClusterAnnouncementBannerImage from '@src/assets/clusterannouncementbannerimage.png';
import useDashboardAnalytics from './hooks/use-dashboard-analytics';
import { isDashboardAllowed } from '@src/shared/utils';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import { useTranslation } from 'react-i18next';
import { useTutorialState } from '@src/shared/context/tutorial';
import { useOnboardingStore } from '@src/shared/store/onboarding/onboarding-store';

const NotSupported = React.lazy(() => import('@pages/NotSupported'));
let feedbackTimer: any;
export const DashboardLayout: FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const { restaurant, isStateLoaded, keycloakToken } = useKeycloakUserState();
  const [isModalOpen, setModalOpen] = useState<Boolean>(false);
  const { params } = useQueryParam(languageQueryParam);
  const { triggerFeedbackCampaign, showFeedback } = useGetFeedback();
  const feedbackRoutes = ['/couriers', '/deliveries'];
  const { isMobileDevice } = useDeviceDetect();
  const { i18n } = useTranslation();
  const { activeTutorial } = useTutorialState();
  const { isPartnerhubSession } = useOnboardingStore();

  useDashboardAnalytics();

  const {
    executeAnnouncementBannerLogic,
    handleOnAnnouncementBannerAction,
    handleOnAnnouncementBannerClose,
    handleOnAnnouncementBannerFormUpdate,
    modalVisible,
  } = useAnnouncementBannerLogic({
    trackingTitle: 'map clusters - announcement',
    logicState: utilStorage.getClusterBannerSeen(),
    onSkip: () => {
      setModalOpen(false);
    },
    onFinalize: () => {
      utilStorage.setClusterBannerSeen('NO_SHOW');
    },
    onShow: (currentShowLogic) => {
      utilStorage.setClusterBannerSeen(JSON.stringify(currentShowLogic));
    },
    onBannerAction: async () => {
      setModalOpen(false);
      utilStorage.setClusterBannerSeen('NO_SHOW');
    },
  });

  const runCachePreventRefresher = () => {
    const url = new URL(window.location.href);
    if (window.localStorage) {
      const lastCheckedVersion = localStorage.getItem('last-checked-version');
      if (MCPComparisonVersionValue === process.env.MCP_VERSION) {
        localStorage.setItem('last-checked-version', MCPComparisonVersionValue);
        return;
      }
      if (!lastCheckedVersion || lastCheckedVersion !== MCPComparisonVersionValue) {
        if (MCPVersionRefresherIsActive) {
          window.location.href = `${url.origin}${window.location.pathname}?v=${MCPComparisonVersionValue}`;
        }
        localStorage.setItem('last-checked-version', MCPComparisonVersionValue);
      }
    }
  };

  useEffect(() => {
    if (params) {
      if (isLanguageActive(params)) {
        i18n.changeLanguage(params);
      }
    }
  }, [params]);

  useEffect(() => {
    if (keycloakToken && location.pathname === '/deliveries' && !isPartnerhubSession) {
      executeAnnouncementBannerLogic();
    }
  }, [keycloakToken, location]);

  useEffect(() => {
    setModalOpen(modalVisible);
  }, [modalVisible]);

  useEffect(() => {
    if (feedbackRoutes?.includes(location.pathname)) {
      showFeedback(true);
    } else {
      showFeedback(false);
    }

    return () => showFeedback(false);
  }, [location.pathname]);

  const isCampaignActive = useFMFeatureFlag(JETFM_ACTIVATE_FEEDBACK_CAMPAIGN);
  const isSurveyActive = useFMFeatureFlag(JETFM_ACTIVATE_FEEDBACK_SURVEY);
  const isMcActive = useFMFeatureFlag(JETFM_SHOW_MANAGE_COURIERS);
  const MCPComparisonVersionValue = useFMFeatureFlag(JETFM_MCP_VERSION_COMPARISON_VALUE);
  const MCPVersionRefresherIsActive = useFMFeatureFlag(JETFM_MCP_VERSION_REFRESHER_IS_ACTIVE_VALUE);
  //const showNewDashboard = useFMFeatureFlag(JETFM_SHOW_NEW_DASHBOARD);
  const fetchNotificationList = useQueryNotificationList({
    onError: () => {
      console.warn('ERROR: Fetching courier list failed');
    },
    onSuccess: () => {},
  });

  useEffect(() => {
    fetchNotificationList;
  }, []);

  useEffect(() => {
    if (restaurant?.country !== undefined && isCampaignActive) {
      triggerFeedbackCampaign(
        'mcp_feedback',
        ['de', 'at', 'pl', 'nl', 'ch', 'fr'],
        restaurant.country
      );
    }
    if (restaurant?.country !== undefined && isSurveyActive) {
      triggerFeedbackCampaign('mcp_survey', ['de', 'nl', 'ch'], restaurant.country);
    }
  }, [restaurant?.country, isCampaignActive, isSurveyActive]);

  useEffect(() => {
    setTimeout(() => {
      if (restaurant?.country === 'pl' || i18n.language === 'pl') {
        window.usabilla_live('trigger', 'low_adoption_poland_mcp_uxr');
      }
    }, 5000);
  }, [i18n.language]);

  useEffect(() => {
    if (location.pathname === '/deliveries' || location.pathname === '/couriers') {
      runCachePreventRefresher();
    }
  }, [MCPComparisonVersionValue, MCPVersionRefresherIsActive]);

  useEffect(() => {
    if (!activeTutorial && !modalVisible) {
      feedbackTimer = setTimeout(() => {
        window.usabilla_live('trigger', `MCP-1957_${i18n.language}`);
      }, 15000);
    } else if (activeTutorial || modalVisible) {
      if (feedbackTimer) {
        clearTimeout(feedbackTimer);
      }
    }
  }, [activeTutorial, modalVisible]);

  return (
    <div className={classes.wrapper}>
      <PIEAnnouncementBanner
        onClose={({ checkboxSelected = false }) => {
          if (checkboxSelected) {
            utilStorage.setClusterBannerSeen('NO_SHOW');
          }
          handleOnAnnouncementBannerClose();
        }}
        onFormUpdate={handleOnAnnouncementBannerFormUpdate}
        onAction={handleOnAnnouncementBannerAction}
        showBanner={isModalOpen}
        closeButtonTitle="announcement_statistics.CLOSE_BUTTON"
        showButtonTitle="announcement_statistics.SHOW_BUTTON"
        title="announcement_statistics.TITLE"
        content="announcement_statistics.CONTENT"
        bannerImage={!isMobileDevice ? ClusterAnnouncementBannerImage : null}
        checkBoxTitle="announcement_statistics.CHECK_BOX"
        headerBackgroundColor="var(--xds-color-container-subtle)"
        showCheckBox={true}
        actionButtonsPositioning={ANNOUNCEMENT_BANNER_BUTTON_POSITIONING.HORIZONTAL}
        closeButtonCSS={{
          backgroundColor: 'var(--xds-color-interactive-secondary)',
          padding: '10px',
          borderRadius: '100px',
          marginRight: '-5px',
          marginTop: '-10px',
          position: 'absolute',
          zIndex: 1000,
        }}
      />

      <Header isPanel={true} fixed={true} />
      <ConnectionNotifier />

      <Content
        isFullScreen={location.pathname === '/deliveries'}
        isHalfScreen={location.pathname === '/couriers'}
        overWriteStyle={isMcActive && location.pathname === '/couriers' ? { padding: '0' } : {}}
      >
        <>
          {isStateLoaded ? (
            <>
              {restaurant?.isCourierPortalEnabled ? (
                <Switch>
                  <Route exact path="/couriers">
                    <Couriers />
                  </Route>
                  <Route exact path="/deliveries">
                    {isDashboardAllowed(restaurant?.country as string) === true ? (
                      <DeliveryDashboard />
                    ) : (
                      <Deliveries />
                    )}
                  </Route>
                  <Route path="*">
                    <Redirect to="/deliveries" />
                  </Route>
                </Switch>
              ) : (
                <>
                  {location.pathname === '/not-supported' ? (
                    <NotSupported />
                  ) : (
                    <>
                      <Redirect to="/not-supported" />
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <SplashScreen />
          )}
        </>
      </Content>
      {location.pathname === '/deliveries' &&
      !!isDashboardAllowed(restaurant?.country as string) ? null : (
        <Footer />
      )}
    </div>
  );
};

export default DashboardLayout;
